import React, {
  createContext,
  useContext,
  PropsWithChildren,
  MutableRefObject,
  useMemo,
} from 'react'
import { DateRange } from 'react-day-picker'

import { AgeCategoryCount, Location, FilterBarTypes } from '../FilterBarTypes'
import { useFilterActions, useFilterState, useFilterRefs } from '../hooks'

type FilterBarProviderProps = PropsWithChildren<FilterBarTypes>

type FilterBarContextType = FilterBarTypes & {
  selectedFilter: string | boolean
  ageCategoryCounts: AgeCategoryCount
  calendarRange?: DateRange
  selectedPath: string
  innerLoading: boolean
  selectedLocations: Location[]

  setSelectedLocations: (val: Location[]) => void
  setCalendarRange: (val: DateRange | undefined) => void
  setSelectedFilter: (val: string | boolean) => void
  setAgeCategoryCounts: (val: AgeCategoryCount) => void
  setSelectedPath: (val: string) => void

  handleSelectedFilter: (id: string | boolean) => void
  handleSubmit: () => void
  updateGuestsCount: (id: string, newCount: number) => void
  handleResetFilters: () => void

  // Refs
  previouslyFocusedButtonRef: MutableRefObject<HTMLButtonElement | null>
  panelRef: MutableRefObject<HTMLDivElement | null>
  buttonRefs: MutableRefObject<Record<string, HTMLButtonElement | null>>
  filtersRef: MutableRefObject<HTMLDivElement | null>
}

const FilterBarContext = createContext<FilterBarContextType | undefined>(
  undefined
)

export const FilterBarProvider = ({
  children,
  language,
  ageCategories,
  redirectUrl,
  palette,
  onSubmit,
  fullWidth,
  disableCalendarDates,
  mode,
  tabs,
  outerLoading,
  locations,
}: FilterBarProviderProps) => {
  const {
    selectedPath,
    selectedFilter,
    calendarRange,
    innerLoading,
    ageCategoryCounts,
    selectedLocations,
    setSelectedLocations,
    setCalendarRange,
    setAgeCategoryCounts,
    setSelectedPath,
    setSelectedFilter,
    setInnerLoading,
  } = useFilterState({ locations })

  const filterActions = useFilterActions({
    tabs,
    ageCategoryCounts,
    ageCategories,
    selectedLocations,
    selectedPath,
    redirectUrl,
    calendarRange,
    setSelectedPath,
    setAgeCategoryCounts,
    setSelectedFilter,
    setCalendarRange,
    onSubmit,
    setInnerLoading,
  })

  const filterRefs = useFilterRefs()

  const contextValue = useMemo(
    () => ({
      selectedFilter,
      ageCategoryCounts,
      calendarRange,
      selectedPath,
      innerLoading,
      selectedLocations,

      setSelectedLocations,
      setCalendarRange,
      setSelectedFilter,
      setAgeCategoryCounts,
      setSelectedPath,

      ...filterActions,

      language,
      ageCategories,
      redirectUrl,
      palette,
      onSubmit,
      fullWidth,
      disableCalendarDates,
      mode,
      tabs,
      outerLoading,
      locations,

      ...filterRefs,
    }),
    [
      selectedFilter,
      ageCategoryCounts,
      calendarRange,
      selectedPath,
      innerLoading,
      selectedLocations,
      language,
      ageCategories,
      redirectUrl,
      palette,
      onSubmit,
      fullWidth,
      disableCalendarDates,
      mode,
      tabs,
      outerLoading,
      locations,
      filterRefs.filtersRef,
    ]
  )

  return (
    <FilterBarContext.Provider value={contextValue}>
      {children}
    </FilterBarContext.Provider>
  )
}

export const useFilterBar = () => {
  const context = useContext(FilterBarContext)
  if (!context) {
    throw new Error('useFilterBar must be used within FilterBarProvider')
  }
  return context
}
